import React, { useState } from 'react';
import axios from 'axios';
import { ensureUTC } from '../../utils/dateUtils';

const CancelRideModal = ({ isOpen, onClose, ride, userType, onCancellationSuccess }) => {
  const [cancellationReason, setCancellationReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRefundInfo, setShowRefundInfo] = useState(false);
  const [refundDetails, setRefundDetails] = useState(null);

  // Calculate time until pickup and time since booking to show refund estimate
  const calculateRefundEstimate = () => {
    if (!ride || userType !== 'rider') return { percentage: 100, amount: ride?.final_price || 0 };

    const now = new Date();
    const pickupTime = new Date(ensureUTC(ride.pickup_datetime));

    // Handle created_at - it might be stored in UTC, so parse it carefully
    let bookingTime;
    if (ride.created_at) {
      // If created_at contains 'Z' or '+', it's already in UTC format
      const createdAtStr = ride.created_at.toString();
      if (createdAtStr.includes('Z') || createdAtStr.includes('+')) {
        bookingTime = new Date(ride.created_at);
      } else {
        // Assume it's UTC and append 'Z'
        bookingTime = new Date(ride.created_at + 'Z');
      }
    } else {
      // Fallback: assume ride was just booked
      bookingTime = now;
    }

    const hoursUntil = (pickupTime - now) / (1000 * 60 * 60);
    const minutesSinceBooking = (now - bookingTime) / (1000 * 60);

    let percentage = 0;
    let scenario = '';

    // 5-tier refund system with booking time priority
    // Use absolute value to handle any timezone issues, but check if it's within 5 minutes
    if (minutesSinceBooking >= 0 && minutesSinceBooking <= 5) {
      // Rule 1: Within 5 minutes of booking - 100% refund
      percentage = 100;
      const mins = Math.max(0, Math.floor(minutesSinceBooking));
      scenario = `within ${mins} minute${mins === 1 ? '' : 's'} of booking`;
    } else if (hoursUntil >= 24) {
      // Rule 2: >= 24 hours from pickup - 100% refund
      percentage = 100;
      scenario = `${Math.floor(hoursUntil)} hours before your ride`;
    } else if (hoursUntil >= 12) {
      // Rule 3: 12-24 hours from pickup - 50% refund
      percentage = 50;
      scenario = `${Math.floor(hoursUntil)} hours before your ride`;
    } else if (hoursUntil >= 6) {
      // Rule 4: 6-12 hours from pickup - 25% refund
      percentage = 25;
      scenario = `${Math.floor(hoursUntil)} hours before your ride`;
    } else {
      // Rule 5: < 6 hours from pickup - 0% refund
      percentage = 0;
      scenario = `${Math.floor(Math.max(0, hoursUntil))} hours before your ride`;
    }

    const baseFare = ride.total_fare || ride.final_price || 0;
    const amount = (baseFare * percentage) / 100;

    return {
      percentage,
      amount,
      hoursUntil: Math.floor(hoursUntil),
      minutesSinceBooking: Math.floor(minutesSinceBooking),
      scenario
    };
  };

  const handleCancel = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');

      // Determine the correct endpoint based on ride type
      let cancelUrl;
      if (ride.is_day_booking_leg) {
        // Day booking leg - use the day booking cancellation endpoint with booking_id
        cancelUrl = `${process.env.REACT_APP_API_URL}/api/day-bookings/rider/${ride.booking_id}/cancel`;
      } else {
        // Regular ride - use the standard rides cancellation endpoint
        cancelUrl = `${process.env.REACT_APP_API_URL}/api/rides/${ride.id}/cancel`;
      }

      const response = await axios.post(
        cancelUrl,
        {
          reason: cancellationReason || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setRefundDetails(response.data);
      setShowRefundInfo(true);

      // Wait a moment for user to see confirmation, then close and refresh
      setTimeout(() => {
        onCancellationSuccess && onCancellationSuccess(response.data);
        handleClose();
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel ride');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCancellationReason('');
    setError(null);
    setShowRefundInfo(false);
    setRefundDetails(null);
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  const refundEstimate = calculateRefundEstimate();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {!showRefundInfo ? (
          <>
            {/* Cancellation Form */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {ride?.is_day_booking_leg ? 'Cancel Day Booking' : 'Cancel Ride'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {ride?.pickup_address || ride?.pickup_location} → {ride?.dropoff_address || ride?.dropoff_location}
                  </p>
                  {ride?.is_day_booking_leg && (
                    <p className="text-xs text-orange-600 mt-1">
                      This will cancel the entire {ride?.rate_type === 'half_day' ? 'half-day' : 'full-day'} booking
                    </p>
                  )}
                </div>
              </div>

              {/* Refund Policy Info for Riders */}
              {userType === 'rider' && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-3">Refund Information</h3>

                  {/* Bold scenario text */}
                  <div className="mb-4 p-3 bg-white border border-blue-300 rounded">
                    <p className="text-base font-bold text-blue-900">
                      You are cancelling {refundEstimate.scenario}, and you are entitled to a {refundEstimate.percentage}% refund
                    </p>
                    <p className="text-sm mt-2 text-blue-800">
                      <strong>Estimated refund:</strong> ${Math.round(refundEstimate.amount)}
                    </p>
                  </div>

                  {/* All 5 cancellation rules in medium-dark grey */}
                  <div className="text-sm text-gray-600 space-y-2">
                    <p className="font-medium mb-2 text-gray-700">Cancellation Policy:</p>
                    <ul className="list-disc list-inside space-y-1.5 pl-2">
                      <li>Within 5 minutes of booking: No charge</li>
                      <li>24+ hours before ride: No charge</li>
                      <li>12-24 hours before ride: 50% of fare charged</li>
                      <li>6-12 hours before ride: 75% of fare charged</li>
                      <li>Less than 6 hours before ride: Full fare charged</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Driver Cancellation Notice */}
              {userType === 'driver' && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> When you cancel a ride, the rider's payment method will not be charged.
                  </p>
                </div>
              )}

              {/* Cancellation Reason */}
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Reason for cancellation (optional)
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  rows="3"
                  placeholder="Let the other party know why you're cancelling..."
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  disabled={loading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Keep Ride
                </button>
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Cancelling...' : 'Confirm Cancellation'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Success Confirmation */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {ride?.is_day_booking_leg ? 'Day Booking Cancelled' : 'Ride Cancelled'}
                </h2>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cancelled by:</span>
                    <span className="font-medium capitalize">{refundDetails?.cancelled_by}</span>
                  </div>
                  {refundDetails?.refund_percentage === 100 && (
                    <p className="text-green-600 text-sm mt-2">
                      Your payment method will not be charged.
                    </p>
                  )}
                  {refundDetails?.refund_percentage > 0 && refundDetails?.refund_percentage < 100 && (
                    <div className="flex justify-between mt-2">
                      <span className="text-gray-600">Cancellation fee:</span>
                      <span className="font-medium text-amber-600">
                        {100 - refundDetails?.refund_percentage}% of fare
                      </span>
                    </div>
                  )}
                  {refundDetails?.refund_percentage === 0 && (
                    <p className="text-amber-600 text-xs mt-2">
                      Full fare charged due to late cancellation (&lt;6 hours before pickup)
                    </p>
                  )}
                </div>
              </div>


              <p className="text-sm text-gray-600 mb-4">
                {userType === 'rider'
                  ? 'The driver has been notified of the cancellation.'
                  : 'The rider has been notified and their payment method will not be charged.'}
              </p>

              <button
                onClick={handleClose}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CancelRideModal;
